import { isSharedPointer } from '@papan/types'
/**
 * Is this row a cross-tenant share POINTER (the `.shared` family)?
 *
 * A `.shared` row is read-only in the editor: its content is the source
 * tenant's bytes (streamed) or its metadata mirror, never a locally-writable
 * blob. The read-only invariant follows the row KIND (`file_type='shared'`),
 * not the extension. Renaming a `.shared` OFF its suffix DETACHES it: the
 * repo chokepoint demotes it to `file_type='file'` — a plain editable file
 * (see `decideShareFileType`, issue #157 v2b) — so a renamed-away row is
 * correctly NOT a pointer here and opens read-write.
 *
 * The `.shared` suffix is kept as a fallback for contexts where the row's
 * `fileType` isn't available yet — e.g. a freshly opened tab whose detail
 * fetch hasn't resolved, where only the tab title (the file name) is known.
 */
export function isSharePointerRow(
  row: { fileType?: string | null; name?: string } | null | undefined,
  fallbackName?: string,
): boolean {
  if (row?.fileType === 'shared') return true
  // Issue #157 v2: a KNOWN non-'shared' file_type is NOT a share pointer, even
  // if the name ends in `.shared` — a demoted share copy or a hand-made
  // `*.shared` file is an ordinary editable file. The `.shared` suffix is a
  // fallback ONLY when file_type is genuinely unknown (e.g. a freshly opened
  // tab whose detail fetch hasn't resolved, where only the title is known).
  if (row && row.fileType != null) return false
  const name = row?.name ?? fallbackName ?? ''
  return isSharedPointer(name)
}

/** mountId must be a `_`-free standard file id (alphanumeric). */
const MOUNT_ID_TOKEN = /^[A-Za-z0-9]+$/

/**
 * Is this id a compound mount-file id (`s_<mount>_<src>`)? — i.e. a
 * VIRTUAL file inside a mounted `.shared`/`.chain` folder. Such files are
 * cross-tenant pointers with no real local row, so they must open in the
 * read-only viewer (streamed from source), never the collab editor.
 *
 * Client-safe pure mirror of `parseMountFileId` (shared/database/mount-id.ts);
 * duplicated rather than imported because `@papan/database` is server-only.
 * Splits on the first two `_`: prefix 's', `_`-free mountId, non-empty src.
 */
export function isMountFileId(id: string | null | undefined): boolean {
  if (!id) return false
  const i1 = id.indexOf('_')
  if (i1 < 0 || id.slice(0, i1) !== 's') return false
  const i2 = id.indexOf('_', i1 + 1)
  if (i2 < 0) return false
  const mountId = id.slice(i1 + 1, i2)
  const sourceFileId = id.slice(i2 + 1)
  return mountId !== '' && sourceFileId !== '' && MOUNT_ID_TOKEN.test(mountId)
}

/**
 * Should this tab open in a read-only share viewer? True for both kinds of
 * cross-tenant pointer: a `.shared` row (by kind/suffix) and a virtual
 * mount child (compound id). The fileId check is synchronous — the
 * dispatcher relies on it before any detail fetch resolves.
 */
export function isReadOnlyShareTab(
  fileId: string | null | undefined,
  row?: { fileType?: string | null; name?: string } | null,
  fallbackName?: string,
): boolean {
  return isMountFileId(fileId) || isSharePointerRow(row, fallbackName)
}
